import React from 'react';
import type { ImageAsset } from '@sanity/types';
import { bgColorClassname } from '@/utils';
import { Image, MaskedImage } from '@/components';

interface ImageWithAlt {
  _key: string;
  alt?: string;
  image: {
    asset: ImageAsset;
  };
  mask?: string;
}

interface GalleryBlockProps {
  backgroundColor?: {
    label: string;
    value: string;
  };
  images: ImageWithAlt[];
  layout: string;
}

function getImageProps(index: number): string {
  switch (index) {
    case 1:
      return 'group-[.left-side]:mt-10 md:group-[.left-side]:mt-0 md:group-[.left-side]:ml-28 lg:group-[.left-side]:ml-44';
    case 2:
      return 'mt-16 group-[.left-side]:mt-0 -ml-9 min-[580px]:mt-16 min-[580px]:-ml-9 md:ml-0 md:group-[.left-side]:-mb-20 lg:group-[.left-side]:-mb-24 md:group-[.left-side]:-mt-[105px] md:group-[.right-side]:-mb-16 md:group-[.right-side]:-mt-32 md:group-[.right-side]:ml-28 lg:group-[.right-side]:ml-36';
    case 3:
      return '-ml-9 -mt-2 group-[.left-side]:mt-12 min-[560px]:group-[.left-side]:mt-16 min-[580px]:-ml-10 min-[480px]:group-[.left-side]:mt-15 md:group-[.left-side]:mt-0 md:group-[.left-side]:ml-24 lg:group-[.left-side]:ml-[140px] md:group-[.right-side]:ml-9 md:mt-0';
    case 4:
      return '-ml-9 mt-9 group-[.left-side]:-mt-4 min-[580px]:-ml-9 min-[580px]:mt-9 min-[580px]:group-[.left-side]:-mt-6 md:group-[.left-side]:-mt-16 md:group-[.left-side]:ml-4 md:group-[.right-side]:ml-28 lg:group-[.right-side]:ml-36 md:group-[.right-side]:-mt-20';
    default:
      return '';
  }
}

const GalleryBlock: React.FC<GalleryBlockProps> = ({ images, layout, backgroundColor }) => {
  return (
    <div
      className={`${backgroundColor ? bgColorClassname(backgroundColor) : ''} flex h-full w-full items-center px-5 py-8 lg:p-[50px]`}
    >
      <div
        className={`group mx-auto flex max-w-screen-xl flex-row justify-center md:flex md:max-w-full md:flex-col md:gap-3 md:gap-x-6 lg:gap-x-12 xl:gap-x-24 ${layout} ${layout === 'left-side' ? 'md:flex-row-reverse' : 'md:flex-row'}`}
      >
        {images &&
          images.map((image, index) => (
            <div
              key={image._key}
              className={`image-${index + 1} ${getImageProps(index + 1)} max-h-[180px] max-w-[180px] sm:h-[180px] sm:max-h-[217px] sm:w-[180px] sm:max-w-[217px] sm:group-[.right-side]:first-of-type:h-[190px] sm:group-[.right-side]:first-of-type:w-[190px] lg:h-[206px] lg:w-[206px] lg:group-[.right-side]:first-of-type:h-[217px] lg:group-[.right-side]:first-of-type:w-[217px]`}
            >
              {image.mask && image.mask != 'no-mask' ? (
                <MaskedImage
                  image={image?.image}
                  mask={image.mask}
                  className='aspect-square w-full'
                  showStroke={true}
                  alt={image?.alt}
                  width={325}
                />
              ) : (
                <Image
                  image={image?.image}
                  alt={image?.alt}
                  className='max-full'
                  width={325}
                />
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default GalleryBlock;
